import React, { useState } from 'react';

import dayjs from 'dayjs';

import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, IconButton, Modal, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { getCurrencySymbolIntl } from '~/components/Refund/RefundV2/Utils/RefundUtils';
import Table from '~/components/v2/common/Table/Table';

import { useFetchPackageInclusions } from '~/hooks/apis/inclusions/useFetchPackageInclusions';
import { BundledExperience, useFetchBundledExperiences } from '~/hooks/apis/led-offers/useFetchBundledExperiences';

const cellStyle = { whiteSpace: 'normal', lineHeight: '1.5' };

const defaultCellRenderer = (params: GridRenderCellParams) => <Box sx={cellStyle}>{params.value || '-'}</Box>;

const dateCellRenderer = (params: GridRenderCellParams) => (
  <Box sx={cellStyle}>{params.value ? dayjs(params.value).format('DD MMM YYYY') : '-'}</Box>
);

const currencyCellRenderer = (params: GridRenderCellParams) => (
  <Box sx={cellStyle}>
    {params.value
      ? params.row.currencyisocode
        ? getCurrencySymbolIntl(params.row.currencyisocode) + params.value
        : params.value
      : '-'}
  </Box>
);

const sharedDataGridProps = {
  initialState: {
    pagination: {
      paginationModel: { page: 0, pageSize: 5 },
    },
  },
  autoHeight: true,
  getRowHeight: () => 'auto' as const,
  sx: {
    '& .MuiDataGrid-cell': {
      padding: '6px',
      whiteSpace: 'normal',
      lineHeight: '1.5',
      alignItems: 'flex-start',
    },
  },
};

const inclusionsColumns: Array<GridColDef> = [
  {
    field: 'name',
    headerName: 'Inclusion Name',
    flex: 285,
    renderCell: defaultCellRenderer,
  },
  {
    field: 'description',
    headerName: 'Description',
    flex: 375,
    renderCell: defaultCellRenderer,
  },
  {
    field: 'rrp',
    headerName: 'RRP',
    flex: 110,
    renderCell: currencyCellRenderer,
  },
  {
    field: 'total_value',
    headerName: 'Total Value',
    flex: 110,
    renderCell: currencyCellRenderer,
  },
  {
    field: 'type',
    headerName: 'Type',
    flex: 110,
    renderCell: defaultCellRenderer,
  },
];

const bundledExperiencesColumns: Array<GridColDef<BundledExperience>> = [
  {
    field: 'name',
    headerName: 'Name',
    flex: 2,
    renderCell: defaultCellRenderer,
  },
  {
    field: 'fare_type_display',
    headerName: 'Fare Type',
    flex: 1,
    renderCell: defaultCellRenderer,
  },
  {
    field: 'rrp',
    headerName: 'RRP',
    flex: 1,
    renderCell: (params) => (
      <Box sx={cellStyle}>{params.value ? `${params.row.currencyisocode} ${params.value.toLocaleString()}` : '-'}</Box>
    ),
  },
  {
    field: 'converted_cost_aud',
    headerName: 'Cost (AUD)',
    flex: 1,
    renderCell: (params) => <Box sx={cellStyle}>{params.value ? `AUD ${params.value}` : '-'}</Box>,
  },
  {
    field: 'sell_price_uplift',
    headerName: 'Sell Price Uplift',
    flex: 1,
    renderCell: (params) => <Box sx={cellStyle}>{params.value ?? '-'}</Box>,
  },
  {
    field: 'book_by_date',
    headerName: 'Book By',
    flex: 1,
    renderCell: dateCellRenderer,
  },
  {
    field: 'travel_to_date',
    headerName: 'Travel To',
    flex: 1,
    renderCell: dateCellRenderer,
  },
  {
    field: 'is_luxplus_exclusive',
    headerName: 'LuxPlus Exclusive',
    flex: 1,
    renderCell: (params) => (
      <Box sx={cellStyle}>{params.value ? <Chip label="Yes" color="primary" size="small" /> : '-'}</Box>
    ),
  },
];

interface Props {
  dealName: string;
  open: boolean;
  dealSalesforceId: string;
  offerId: string;
  handleClose: () => void;
  currencyisocode: string;
}

export default function OpportunityDealInclusionsModal({
  dealName,
  open,
  offerId,
  dealSalesforceId,
  handleClose,
  currencyisocode,
}: Props) {
  const [inclusionsExpanded, setInclusionsExpanded] = useState(true);
  const [bundledExpanded, setBundledExpanded] = useState(true);

  const { data: inclusions = [], isLoading: isInclusionsLoading } = useFetchPackageInclusions({
    offerId,
    dealSalesforceId,
    currencyisocode,
    enabled: !!offerId && open,
  });

  const { data: bundledExperiences = [], isLoading: isBundledLoading } = useFetchBundledExperiences({
    dealSalesforceId,
    enabled: !!dealSalesforceId && open,
  });

  if (!open) {
    return null;
  }

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="modal-deal-title">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 1400,
          maxWidth: '95vw',
          backgroundColor: 'background.paper',
          boxShadow: 24,
          p: 4,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ mb: 2 }}>
          <Typography id="modal-deal-title" variant="h5" component="h2">
            {dealName}
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Accordion expanded={inclusionsExpanded} onChange={() => setInclusionsExpanded(!inclusionsExpanded)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Inclusions ({inclusions?.length ?? 0})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Table
                columns={inclusionsColumns}
                rows={inclusions}
                getRowId={(row) => row.id}
                paginationMode="client"
                isDisabled
                isLoading={isInclusionsLoading}
                dataGridProps={sharedDataGridProps}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion expanded={bundledExpanded} onChange={() => setBundledExpanded(!bundledExpanded)} sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Bundled Experiences ({bundledExperiences?.length ?? 0})</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ overflowX: 'auto' }}>
              <Table
                columns={bundledExperiencesColumns}
                rows={bundledExperiences}
                getRowId={(row) => row.rowIndex}
                paginationMode="client"
                isDisabled
                isLoading={isBundledLoading}
                dataGridProps={{
                  ...sharedDataGridProps,
                  sx: {
                    ...sharedDataGridProps.sx,
                    '& .MuiDataGrid-virtualScroller': {
                      overflowX: 'auto',
                    },
                  },
                }}
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
    </Modal>
  );
}
